<script lang="ts">
  import type { StashColorNamesWithShades } from '@leaflink/stash-types/colors';
  import {
    type Cell,
    type Column,
    type ColumnDef as ColDef,
    type ColumnDef,
    type ColumnPinningState,
    FlexRender as flexRender,
    type Header,
    type Row as TRow,
    type TableOptions,
  } from '@tanstack/vue-table';

  import Table, { type TableProps } from '../Table/Table.vue';

  export interface DataTableProps<TData = unknown> extends Omit<
    TableProps,
    'isSchemaDriven' | 'isSelectable' | 'layout'
  > {
    /**
     * TanStack column definitions (flat leaf columns only).
     * Nested `columns` / column groups are not supported—only a single header row is rendered.
     */
    columns: ColumnDef<TData, any>[];

    /** Row data for the TanStack table instance. */
    data: TData[];

    /**
     * TanStack initialState. Use `initialState.columnPinning` to pin columns left/right.
     * Shape: `{ columnPinning?: { left?: string[]; right?: string[] } }`.
     * When any column is pinned left, `select` and `expand` columns (if enabled) are automatically pinned left.
     *
     * **`columnVisibility`** and **`columnOrder`** on `initialState` are ignored—use **`v-model:column-ui`** (hydrate the bound ref when loading from storage).
     */
    initialState?: TableOptions<TData>['initialState'];

    /**
     * When true, a checkbox selection column is prepended and the table uses TanStack row selection (current page only).
     */
    isSelectable?: boolean;

    /**
     * Returns a stable id for each row. When not provided, row index is used.
     */
    getRowId?: (row: TData) => string;

    /**
     * Returns whether the row can be selected. When not provided, all rows are selectable.
     */
    getRowCanSelect?: (row: import('@tanstack/vue-table').Row<TData>) => boolean;

    /**
     * Returns an accent color for the given row. When provided, the result is passed to TableRow for the left-edge accent bar.
     */
    getRowAccentColor?: (row: TData) => StashColorNamesWithShades | undefined;

    /**
     * Returns child rows for hierarchical/sub-row expansion.
     */
    getSubRows?: (row: TData) => TData[] | undefined;

    /**
     * Controls which rows can be expanded. Defaults to TanStack's built-in check (has sub-rows).
     * Return `true` for every row to allow custom expansion content via the `#expansion` slot.
     */
    getRowCanExpand?: (row: TRow<TData>) => boolean;

    /**
     * When true, hides the divider between each row and its expanded content.
     */
    hideExpansionDivider?: boolean;
  }
</script>

<script setup lang="ts" generic="TData = unknown">
  import { getBreakpointMinWidthMediaQuery } from '@leaflink/stash-utils';
  import { useSortable } from '@vueuse/integrations/useSortable';
  import type { SortableEvent } from 'sortablejs';
  import { computed, h, nextTick, ref, shallowRef, unref, useCssModule, useTemplateRef, watch, watchEffect } from 'vue';

  import useDataTable from '../../composables/useDataTable/useDataTable';
  import useMediaQuery from '../../composables/useMediaQuery/useMediaQuery';
  import { t } from '../../locale';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import ChevronToggle from '../ChevronToggle/ChevronToggle.vue';
  import Icon from '../Icon/Icon.vue';
  import Paginate from '../Paginate/Paginate.vue';
  import TableCell from '../TableCell/TableCell.vue';
  import TableHeaderCell from '../TableHeaderCell/TableHeaderCell.vue';
  import TableHeaderRow from '../TableHeaderRow/TableHeaderRow.vue';
  import TableRow from '../TableRow/TableRow.vue';
  import {
    dataTableColumnOrderWithHiddenAppended,
    isDataTableHeaderReorderable,
    mergeReorderableIntoVisibleOrder,
    reorderableColumnIdsFromHeaderRow,
  } from './dataTableColumnReorder';
  import type { DataTableColumnUiState } from './DataTableColumnUi.types';

  defineOptions({ inheritAttrs: false });

  const props = withDefaults(defineProps<DataTableProps<TData>>(), {
    initialState: undefined,
    isSelectable: false,
    getRowId: undefined,
    getRowCanSelect: undefined,
    getRowAccentColor: undefined,
    getSubRows: undefined,
    getRowCanExpand: undefined,
    density: undefined,
    emptyStateText: '',
    hasActions: false,
    hasCustomExpandToggle: false,
    isEmpty: false,
    isLoading: false,
    isExpandable: false,
    layout: 'scroll',
    radius: 'rounded',
    stickyHeader: undefined,
    hideExpansionDivider: false,
  });

  /** Column UI for persistence (`localStorage`, etc.). Use `v-model:column-ui`. Omitted keys use TanStack defaults. */
  const columnUi = defineModel<DataTableColumnUiState>('columnUi', {
    default: () => ({}),
  });

  const classes = useCssModule();

  const HEADER_REORDER_HANDLE_CLASS = 'stash-column-reorder-handle';
  const isMdUp = useMediaQuery(getBreakpointMinWidthMediaQuery('md'));
  const slots = defineSlots<{
    empty?(): unknown[];
    expansion?(props: { row: TRow<TData> }): unknown[];
  }>();

  const expandColumnDef = computed<ColDef<TData, unknown>>(() => ({
    id: 'expand',
    size: 48,
    minSize: 48,
    maxSize: 48,
    enableHiding: false,
    meta: {
      class: `${classes['row-control-cell']} stash-table-cell__is-expansion-cell px-0!`,
    },
    header: () => null,
    cell: ({ row }: { row: TRow<TData> }) =>
      row.getCanExpand()
        ? h(ChevronToggle, {
            'aria-label': row.getIsExpanded() ? t('ll.table.collapseRow') : t('ll.table.expandRow'),
            direction: row.getIsExpanded() ? 'up' : 'down',
            onClick: () => row.toggleExpanded(),
          })
        : null,
  }));

  const selectionColumnDef = computed<ColDef<TData, unknown>>(() => ({
    id: 'select',
    size: 48,
    minSize: 48,
    maxSize: 48,
    enableHiding: false,
    meta: {
      class: `${classes['row-control-cell']} stash-table-cell__is-selection-cell lg:pl-3`,
    },
    header: ({ table }: { table: import('@tanstack/vue-table').Table<TData> }) =>
      h(Checkbox, {
        checked: table.getIsAllPageRowsSelected(),
        indeterminate: table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected(),
        title: t('ll.selectAll'),
        class: classes['row-selection-checkbox'],
        'onUpdate:checked': () => table.toggleAllPageRowsSelected(),
      }),
    cell: ({ row }: { row: TRow<TData> }) =>
      h(Checkbox, {
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        title: t('ll.select.self'),
        class: classes['row-selection-checkbox'],
        'onUpdate:checked': () => row.toggleSelected(),
      }),
  }));

  const effectiveColumns = computed<ColDef<TData, unknown>[]>(() => {
    const cols = [...props.columns];
    if (props.isExpandable && !props.hasCustomExpandToggle) cols.unshift(expandColumnDef.value);
    if (props.isSelectable) cols.unshift(selectionColumnDef.value);
    return cols;
  });

  const mergedInitialState = computed<TableOptions<TData>['initialState']>(() => {
    const base = props.initialState ?? {};
    const pinning: ColumnPinningState = {
      left: [...(base.columnPinning?.left ?? [])],
      right: [...(base.columnPinning?.right ?? [])],
    };

    if (pinning.left!.length > 0) {
      const hasExpand = props.isExpandable && !props.hasCustomExpandToggle;
      if (hasExpand && !pinning.left!.includes('expand')) pinning.left!.unshift('expand');
      if (props.isSelectable && !pinning.left!.includes('select')) pinning.left!.unshift('select');
    }

    return { ...base, columnPinning: pinning };
  });

  const dataTableResult = useDataTable<TData>({
    columns: effectiveColumns,
    data: computed(() => props.data),
    initialState: mergedInitialState.value,
    columnUi,
    getSubRows: props.getSubRows,
    getRowCanExpand: props.getRowCanExpand,
    getRowId: props.getRowId,
    ...(props.isSelectable
      ? {
          enableRowSelection: props.getRowCanSelect ? (row) => props.getRowCanSelect!(row) : undefined,
        }
      : {}),
  });

  const { table } = dataTableResult;

  /** Single header row only; column groups are not supported. */
  const flatHeaderGroup = computed(() => table.getHeaderGroups()[0] ?? null);

  // build simple list of reorderable ids from table for useSortable
  function buildReorderableIdsFromTable(): string[] {
    const g = flatHeaderGroup.value;
    if (!g) return [];
    return g.headers.filter((h) => isDataTableHeaderReorderable(h)).map((h) => h.column.id);
  }

  // update tanstack column order state from reorderable ids
  // after a drag end
  function commitColumnOrderFromReorderableList(): void {
    const headerGroup = flatHeaderGroup.value;
    if (!headerGroup) return;
    const mergedIds = mergeReorderableIntoVisibleOrder({
      visibleHeaders: headerGroup.headers,
      newReorderableIds: [...headerReorderableIds.value],
      isReorderable: isDataTableHeaderReorderable,
    });
    table.setColumnOrder(
      dataTableColumnOrderWithHiddenAppended({
        mergedVisibleColumnIds: mergedIds,
        allLeafColumnIds: table.getAllLeafColumns().map((c) => c.id),
      }),
    );
  }

  const headerReorderableIds = shallowRef<string[]>([]);
  const isColumnDragging = ref(false);
  const sortableRootRef = ref<HTMLElement | null>(null);
  const headerRowRef = useTemplateRef('headerRowRef');

  // set the sortable root element to the first header row element when it becomes available
  watchEffect(() => {
    const headerRow = headerRowRef.value;
    const first = Array.isArray(headerRow) ? headerRow[0] : headerRow;
    const row = first?.rowElement;
    sortableRootRef.value = row != null ? unref(row) : null;
  });

  // keep the reorderable ids in sync with the table column order
  // e.g. when a column is hidden or shown
  watchEffect(() => {
    void flatHeaderGroup.value;
    void table.getState().columnOrder;
    if (isColumnDragging.value) return;
    headerReorderableIds.value = buildReorderableIdsFromTable();
  });

  const hasAnyReorderableColumn = computed(() => {
    if (!isMdUp.value) return false;
    const g = flatHeaderGroup.value;
    return g?.headers.some((h) => isDataTableHeaderReorderable(h)) ?? false;
  });

  const sortableJsOptions = {
    draggable: '[data-stash-column-reorder]',
    handle: `.${HEADER_REORDER_HANDLE_CLASS}`,
    animation: 150,
    ghostClass: classes['header-reorder-sortable-ghost'],
    chosenClass: classes['header-reorder-sortable-chosen'],
    onStart() {
      isColumnDragging.value = true;
    },
    onUpdate() {
      const row = sortableRootRef.value;
      if (row == null) return;
      headerReorderableIds.value = reorderableColumnIdsFromHeaderRow(row);
    },
    onEnd(evt: SortableEvent) {
      if (evt.oldIndex === evt.newIndex) {
        isColumnDragging.value = false;
        headerReorderableIds.value = buildReorderableIdsFromTable();
        return;
      }

      void nextTick(() => {
        commitColumnOrderFromReorderableList();
        isColumnDragging.value = false;
        headerReorderableIds.value = buildReorderableIdsFromTable();
      });
    },
  };

  const sortableApi = useSortable(sortableRootRef, headerReorderableIds, sortableJsOptions);

  watch(
    [sortableRootRef, hasAnyReorderableColumn],
    () => {
      sortableApi.stop();
      void nextTick(() => {
        if (sortableRootRef.value && hasAnyReorderableColumn.value) {
          sortableApi.start();
        }
      });
    },
    { flush: 'post' },
  );

  const pinnedLeftWidth = computed(() =>
    isMdUp.value ? table.getLeftLeafColumns().reduce((sum, col) => sum + col.getSize(), 0) : 0,
  );
  const pinnedRightWidth = computed(() =>
    isMdUp.value ? table.getRightLeafColumns().reduce((sum, col) => sum + col.getSize(), 0) : 0,
  );
  const tableMinWidth = computed(() => (table.getIsSomeColumnsPinned() ? table.getTotalSize() : undefined));

  const showPagination = computed(() => {
    if (dataTableResult.isServerMode) return false;
    return dataTableResult.table.getPageCount() > 1;
  });

  function getColumnSizeStyle(def: { minSize?: number; maxSize?: number }): Record<string, string> | undefined {
    const minSize = def.minSize;
    const maxSize = def.maxSize;
    const style: Record<string, string> = {};
    if (minSize != null && minSize > 0) style.minWidth = `${minSize}px`;
    if (maxSize != null && maxSize < Number.MAX_SAFE_INTEGER) style.maxWidth = `${maxSize}px`;
    return Object.keys(style).length ? style : undefined;
  }

  function getCommonPinningStyles(column: Column<TData, unknown>): Record<string, string> {
    if (!isMdUp.value) return {};
    const isPinned = column.getIsPinned();
    if (!isPinned) return {};

    const style: Record<string, string> = {
      position: 'sticky',
      width: `${column.getSize()}px`,
      zIndex: '1',
    };

    if (isPinned === 'left') {
      style.left = `${column.getStart('left')}px`;
    } else {
      style.right = `${column.getAfter('right')}px`;
    }

    return style;
  }

  function getHeaderColumnStyle(header: Header<TData, unknown>): Record<string, string> | undefined {
    const pinStyles = getCommonPinningStyles(header.column);
    const zIndex = pinStyles.position === 'sticky' && header.column.getIsPinned() ? '2' : '1';
    return { ...getColumnSizeStyle(header.column.columnDef), ...pinStyles, zIndex };
  }

  function getHeaderColumnClass(header: Header<TData, unknown>): string {
    const meta = header.column.columnDef.meta;
    const base = meta?.headerClass ?? meta?.class ?? '';
    if (header.column.id === 'select') {
      return `stash-table-header-row__selection-cell min-w-12 ${classes['row-control-cell']} ${base}`.trim();
    }
    if (header.column.id === 'expand') {
      return `${classes['row-control-cell']} ${base}`.trim();
    }
    const pin = header.column.getIsPinned();
    const pinnedBorder =
      isMdUp.value && pin === 'left' && header.column.getIsLastColumn('left')
        ? 'border-r border-ice-100'
        : isMdUp.value && pin === 'right' && header.column.getIsFirstColumn('right')
          ? 'border-l border-ice-100'
          : '';
    return `${base} ${pinnedBorder}`.trim();
  }

  function getCellColumnStyle(cell: Cell<TData, unknown>): Record<string, string> | undefined {
    return { ...getColumnSizeStyle(cell.column.columnDef), ...getCommonPinningStyles(cell.column) };
  }

  function getCellColumnClass(cell: Cell<TData, unknown>): string {
    const meta = cell.column.columnDef.meta;
    const base = meta?.cellClass ?? meta?.class ?? '';
    const pin = cell.column.getIsPinned();
    const pinnedBorder =
      isMdUp.value && pin === 'left' && cell.column.getIsLastColumn('left')
        ? 'border-r! border-ice-100!'
        : isMdUp.value && pin === 'right' && cell.column.getIsFirstColumn('right')
          ? 'border-l! border-ice-100!'
          : '';
    return `${base} ${pinnedBorder}`.trim();
  }

  const selectedRows = computed(() => dataTableResult.table.getSelectedRowModel().rows);
  const selectedItems = computed(() => selectedRows.value.map((r) => r.original));
  const allPageRowsSelected = computed(() => dataTableResult.table.getIsAllPageRowsSelected());
  const somePageRowsSelected = computed(() => dataTableResult.table.getIsSomePageRowsSelected());

  defineExpose({
    table: dataTableResult.table,
    selectedRows,
    selectedItems,
    allPageRowsSelected,
    somePageRowsSelected,
  });
</script>

<template>
  <Table
    v-bind="$attrs"
    is-schema-driven
    layout="scroll"
    :density="props.density"
    :empty-state-text="props.emptyStateText"
    :has-actions="props.hasActions"
    :has-custom-expand-toggle="props.isExpandable || props.hasCustomExpandToggle"
    :is-empty="props.isEmpty"
    :is-loading="props.isLoading"
    :is-expandable="props.isExpandable"
    :is-selectable="props.isSelectable"
    :radius="props.radius"
    :sticky-header="props.stickyHeader"
    :pinned-left-width="pinnedLeftWidth"
    :pinned-right-width="pinnedRightWidth"
    :table-min-width="tableMinWidth"
  >
    <template #head>
      <TableHeaderRow
        v-if="flatHeaderGroup"
        :key="flatHeaderGroup.id"
        ref="headerRowRef"
        :class="hasAnyReorderableColumn && isColumnDragging ? 'cursor-grabbing' : undefined"
        :data-column-reordering="hasAnyReorderableColumn && isColumnDragging ? 'true' : undefined"
      >
        <TableHeaderCell
          v-for="header in flatHeaderGroup.headers"
          :key="header.id"
          :schema-driven-header="header"
          :data-column-id="header.column.id"
          :data-stash-column-reorder="isMdUp && isDataTableHeaderReorderable(header) ? 'true' : undefined"
          :class="getHeaderColumnClass(header)"
          :style="getHeaderColumnStyle(header)"
        >
          <div class="flex items-center gap-1 min-w-0">
            <button
              v-if="isMdUp && isDataTableHeaderReorderable(header)"
              type="button"
              :class="[
                HEADER_REORDER_HANDLE_CLASS,
                'cursor-grab shrink-0 touch-none rounded-md leading-none hover:bg-ice-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-500 active:cursor-grabbing',
              ]"
              aria-label="Reorder column"
              title="Reorder column"
              @click.stop.prevent
            >
              <Icon name="reorder" size="dense" class="text-ice-600 shrink-0" />
            </button>
            <flexRender :render="header.column.columnDef.header" :props="header.getContext()" />
          </div>
        </TableHeaderCell>
      </TableHeaderRow>
    </template>

    <template #body>
      <TableRow
        v-for="row in dataTableResult.table.getRowModel().rows"
        :key="row.id"
        :accent-color="props.getRowAccentColor?.(row.original)"
        :is-expanded="row.getIsExpanded()"
        :hide-expansion-divider="props.hideExpansionDivider"
      >
        <template #default>
          <TableCell
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :is-control="cell.column.id === 'select' || cell.column.id === 'expand'"
            :class="getCellColumnClass(cell)"
            :style="getCellColumnStyle(cell)"
          >
            <flexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
          </TableCell>
        </template>
        <template v-if="slots.expansion" #expansion>
          <slot name="expansion" :row="row" />
        </template>
      </TableRow>
    </template>

    <template v-if="$slots.empty" #empty>
      <slot name="empty" />
    </template>
  </Table>

  <Paginate
    v-if="showPagination"
    class="mt-6"
    :current-page="dataTableResult.table.getState().pagination.pageIndex + 1"
    :list-length="dataTableResult.table.getFilteredRowModel().rows.length"
    :page-size="dataTableResult.table.getState().pagination.pageSize"
    @set-page="(page: number) => dataTableResult.table.setPageIndex(page - 1)"
  />
</template>

<style module>
  @reference "../../../styles/main.css";

  .row-selection-checkbox label {
    padding: 0;
    min-height: --spacing(6);
    vertical-align: middle;
  }

  .row-control-cell {
    border-right: 0;
  }

  /** Single class names for SortableJS (classList tokens — no spaces). */
  .header-reorder-sortable-ghost {
    @apply max-h-18 max-w-[min(28rem,90vw)] overflow-hidden rounded-md text-ellipsis bg-ice-50 opacity-80;
  }

  .header-reorder-sortable-chosen {
    @apply relative z-3 bg-ice-50 opacity-95 ring-2 ring-ice-200;
  }
</style>
